import {useEffect, useRef, useState} from 'react';
import Analytics from 'utils/Analytics';
import {RiseHeader} from 'screens/InsomniaSurvey/components/RiseHeader/RiseHeader';
import {OsaShell} from 'surveys/osa_funnel/components/OsaShell';
import {useGlp1Modality} from 'surveys/osa_funnel/hooks/useGlp1Modality';
import {readSeverityFromUrl} from 'surveys/osa_funnel/survey/severity';
import {MEMBERSHIP_PRICE, PAY_TODAY_AMOUNT, PAYWALL_COPY, PROMO_DISCOUNT, modalityToVariant} from './paywallCopy';
import {selectFaqClusters, renderParagraph} from './paywallFaq';
import {OutcomesChart} from './OutcomesChart';
import {Glp1PaymentBlock} from './Glp1PaymentBlock';
import styles from './OsaPaywall.module.css';

const TimelineIcon = ({step}: {step: number}) => {
  // Step 0 (Today): clipboard. Step 1 (Day 1–2): clinician person.
  // Step 2 (Day 3–4): package. Step 3 (Ongoing): chat bubble.
  switch (step) {
    case 0:
      return (
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
          <rect x="6" y="3" width="12" height="18" rx="2" />
          <path d="M9 7h6 M9 11h6 M9 15h4" strokeLinecap="round" />
        </svg>
      );
    case 1:
      return (
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
          <circle cx="12" cy="8" r="3.5" />
          <path d="M5 21c0-4 3-7 7-7s7 3 7 7" strokeLinecap="round" />
        </svg>
      );
    case 2:
      return (
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
          <path d="M3 8 L12 4 L21 8 L12 12 Z" strokeLinejoin="round" />
          <path d="M3 8 V16 L12 20 L21 16 V8" strokeLinejoin="round" />
          <path d="M12 12 V20" />
        </svg>
      );
    case 3:
      return (
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
          <path
            d="M4 6c0-1.1.9-2 2-2h12c1.1 0 2 .9 2 2v9c0 1.1-.9 2-2 2H9l-5 4V6z"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      );
    default:
      return null;
  }
};

const CheckIcon = ({className}: {className?: string}) => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M5 12 L10 17 L19 7" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Down-chevron SVG used by FAQ cluster headers. Rotates 180° on
 *  expand via the [aria-expanded='true'] CSS selector. */
const ChevronDown = ({className}: {className?: string}) => (
  <svg width={14} height={14} viewBox="0 0 14 14" fill="none" aria-hidden="true" className={className}>
    <path d="M3 5 L 7 9 L 11 5" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Glp1 paywall — variant-aware (branded/compounded). Renders the dark
 *  takeover paywall: hero → outcomes chart → timeline → pricing → CTA →
 *  reassurance → FAQ → sticky CTA.
 *
 *  Real Stripe + Apple Pay wiring lands in a follow-up commit. For now
 *  CTAs fire analytics + navigate to a confirmation route. */
export const OsaPaywall = () => {
  const modality = useGlp1Modality();
  const variant = modalityToVariant(modality);
  const copy = PAYWALL_COPY[variant];
  const faqClusters = selectFaqClusters(variant);
  const [openClusters, setOpenClusters] = useState<Set<string>>(new Set([faqClusters[0]?.key]));
  const faqRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    Analytics.track('GLP-1 Funnel Paywall Viewed', {
      variant,
      severity: readSeverityFromUrl(),
    });
  }, [variant]);

  const toggleCluster = (key: string) => {
    setOpenClusters(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    // Same funnel shell as the survey / screening: the osa-shell card +
    // RiseHeader. The payment CTA lives inline in the pricing card (the
    // native Apple Pay button), so there's no pinned footer.
    <OsaShell header={<RiseHeader backButton={false} enableCategoryHeader={false} />}>
      <div className={styles.scroll}>
        {/* Hero */}
        <header className={styles.hero}>
          <h1 className={styles.heroHeadline}>{copy.hero.headline}</h1>
          <p className={styles.heroSub}>{copy.hero.sub}</p>
        </header>

        {/* Outcomes carousel — 5 auto-cycling panels with pill tabs. */}
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>{copy.carouselTitle}</h2>
          <OutcomesChart />
        </section>

        {/* Timeline */}
        <section className={`${styles.card} ${styles.cardSpacer}`}>
          <h2 className={styles.cardTitle}>{copy.timeline.title}</h2>
          <p className={styles.cardSub}>{copy.timeline.sub}</p>
          <div className={styles.timelineList}>
            {copy.timeline.steps.map((step, i) => (
              <div key={step.when} className={styles.timelineStep}>
                <span className={`${styles.timelineDot} ${step.gold ? styles.timelineDotGold : ''}`}>
                  <TimelineIcon step={i} />
                </span>
                <div className={styles.timelineStepText}>
                  <div className={`${styles.timelineWhen} ${step.gold ? '' : styles.timelineWhenPurple}`}>
                    {step.when}
                  </div>
                  <p className={styles.timelineDesc}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Pricing card */}
        <section className={`${styles.card} ${styles.cardSpacer}`}>
          <div className={styles.pricingTitleRow}>
            <h3 className={styles.pricingTitle}>RISE Expert Care membership</h3>
            <span className={styles.pricingMembership}>${MEMBERSHIP_PRICE}</span>
          </div>
          <p className={styles.pricingDesc}>{copy.pricing.membershipDesc}</p>

          <div className={styles.pricingPromoRow}>
            <span className={styles.pricingPromoText}>First month promo</span>
            <span className={styles.pricingPromoTag}>−${PROMO_DISCOUNT}</span>
          </div>

          <div className={styles.pricingPayTodayRow}>
            <span className={styles.pricingPayTodayLabel}>Pay today</span>
            <span className={styles.pricingPayTodayAmount}>${PAY_TODAY_AMOUNT}</span>
          </div>

          <p className={styles.pricingDisclosure}>{copy.pricing.payTodayDisclosure}</p>

          <div className={styles.reassurance}>
            {copy.reassurance.map(line => (
              <div key={line} className={styles.reassuranceRow}>
                <CheckIcon className={styles.reassuranceCheck} />
                <span>{line}</span>
              </div>
            ))}
          </div>

          <div className={styles.paymentBlock}>
            <Glp1PaymentBlock ctaLabel={copy.ctaPrimary} />
          </div>
        </section>

        <button
          type="button"
          className={styles.secondaryCta}
          onClick={() => faqRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'})}
        >
          {copy.ctaSecondary} ↓
        </button>

        {/* FAQ */}
        <h2 ref={faqRef} className={styles.faqIntro}>
          Frequently asked <span className="osa-paywall-gold">questions.</span>
        </h2>

        {faqClusters.map(cluster => {
          const expanded = openClusters.has(cluster.key);
          return (
            <div key={cluster.key} className={styles.faqCluster}>
              <button
                type="button"
                className={styles.faqClusterHeader}
                aria-expanded={expanded}
                onClick={() => toggleCluster(cluster.key)}
              >
                <span>{cluster.label}</span>
                <ChevronDown className={styles.faqChevron} />
              </button>
              {expanded ? (
                <div className={styles.faqClusterBody}>
                  {cluster.items.map(item => (
                    <div key={`${cluster.key}-${item.q}`} className={styles.faqItem}>
                      <h4 className={styles.faqQuestion}>{item.q}</h4>
                      <div className={styles.faqAnswer}>{item.a.map((para, i) => renderParagraph(para, i))}</div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}

        <p className={styles.medFootnote}>*{copy.pricing.medFootnote}</p>

        {copy.showFdaApprovedFooter ? (
          <div className={styles.footerCenter}>
            <span className={styles.fdaFooter}>FDA APPROVED</span>
          </div>
        ) : null}
      </div>
    </OsaShell>
  );
};

export default OsaPaywall;
